'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Fired after a programmatic hash write. `history.pushState`/`replaceState` do
 * NOT emit `hashchange`, so we self-notify subscribers on every `setHash`.
 */
const HASH_EVENT = 'safha:hashchange';

function subscribe(callback: () => void) {
  window.addEventListener('hashchange', callback);
  window.addEventListener('popstate', callback); // browser Back/Forward
  window.addEventListener(HASH_EVENT, callback);
  return () => {
    window.removeEventListener('hashchange', callback);
    window.removeEventListener('popstate', callback);
    window.removeEventListener(HASH_EVENT, callback);
  };
}

/** Current fragment, without the leading `#`. */
function getSnapshot(): string {
  return window.location.hash.replace(/^#/, '');
}

/**
 * Server render and first client paint both see an empty fragment, so
 * hydration matches ("all overlays closed"); the real hash is adopted after
 * commit — opening any deep-linked overlay without a mismatch warning.
 */
function getServerSnapshot(): string {
  return '';
}

// Guards the one-time deep-link correction below against running more than
// once per hard page load (across every `useUrlHash` consumer, and against
// React Strict Mode's double-invoked effects) — reset naturally on the next
// full navigation since the module re-evaluates from scratch.
let didCorrectDeepLinkedHash = false;

export type HashHistoryMode = 'push' | 'replace';

export interface ParsedHash {
  key: string;
  params: string[];
}

/**
 * Parse a raw fragment into a key + positional params.
 * `print/qr` → `{ key: 'print', params: ['qr'] }`; empty → `null`.
 */
export function parseHash(raw: string): ParsedHash | null {
  const clean = raw.replace(/^#/, '').trim();
  if (!clean) return null;
  const [key, ...params] = clean.split('/').map((segment) => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  });
  return key ? { key, params } : null;
}

/** Inverse of {@link parseHash}. `('print', ['qr'])` → `print/qr`. */
export function serializeHash(key: string, params: string[] = []): string {
  return [key, ...params].map(encodeURIComponent).join('/');
}

/**
 * Read/write the URL hash fragment as SSR-safe reactive state — the single
 * source of truth for hash-driven overlays. Writes go through `window.history`
 * (not the i18n router): a fragment change is not route navigation, next-intl's
 * router has no fragment API, and the `/ar` locale prefix already lives in
 * `pathname`, so `pushState` preserves it losslessly. `pushState` also does not
 * scroll the page (unlike `location.hash = …`) — correct when opening a modal.
 */
export function useUrlHash() {
  const hash = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Landing directly on a URL that already carries an overlay hash (a deep
  // link, or a refresh) skips `setHash` entirely — the hash is adopted purely
  // by re-rendering with the real client snapshot, so `history.pushState`
  // never runs for it. Next.js's App Router patches `pushState`/`replaceState`
  // to attach its own router-cache bookkeeping to whichever entry is current
  // when that happens; without a write of our own for it to hook into, it
  // leaves a stale duplicate entry that silently eats the next browser Back
  // press instead of closing the overlay. Re-asserting the current URL once,
  // right after hydration, gives it a clean entry to attach to.
  useEffect(() => {
    if (didCorrectDeepLinkedHash) return;
    didCorrectDeepLinkedHash = true;
    if (!window.location.hash) return;
    window.history.replaceState(null, '', window.location.href);
  }, []);

  const setHash = useCallback(
    (next: string, mode: HashHistoryMode = 'push') => {
      const clean = next.replace(/^#/, '');
      const { pathname, search } = window.location;
      const url = clean
        ? `${pathname}${search}#${clean}`
        : `${pathname}${search}`;
      // Carry the current entry's state (Next.js's app-router tags it with
      // `__NA` + its internal tree) forward instead of passing `null`. Next
      // patches `pushState`/`replaceState` to fast-path any call whose state
      // already has `__NA` (see app-router.js); a `null` state misses that
      // check, so Next instead dispatches an async ACTION_RESTORE that later
      // fires its own `replaceState` on the entry we just wrote — racing our
      // push and leaving a stale/duplicate entry that eats the next browser
      // Back/Forward press instead of landing on the plain route.
      const state = window.history.state;
      if (mode === 'replace') {
        window.history.replaceState(state, '', url);
      } else {
        window.history.pushState(state, '', url);
      }
      window.dispatchEvent(new Event(HASH_EVENT));
    },
    []
  );

  return { hash, setHash } as const;
}
